import useContainerWidth from "@/hooks/useContainerWidth";
import { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { ZoomableImage } from "@/components/ZoomableImage";

interface PDFViewerProps {
  url: string;
  targetPageNumber: number;
  onDocumentLoadSuccess?: ({ numPages }: { numPages: number }) => void;
}

const PDFCustomViewer = ({ url, targetPageNumber, onDocumentLoadSuccess }: PDFViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);
  const [totalPages, setTotalPages] = useState<number>(0);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const onDocumentLoadSuccessHandler = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    onDocumentLoadSuccess?.({ numPages });
  };

  // Smooth scroll to target page
  useEffect(() => {
    if (targetPageNumber && totalPages && pageRefs.current[targetPageNumber - 1]) {
      setTimeout(() => {
        pageRefs.current[targetPageNumber - 1]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [targetPageNumber, totalPages]);

  return (
    <div ref={containerRef} className="relative flex h-full w-full items-center justify-center overflow-hidden bg-gray-300">
      {url ? (
        <ZoomableImage resetKey={url} showControls={true} requireModifierForWheelZoom={true} className="h-full w-full">
          <div className="flex h-full flex-col overflow-y-auto p-6 [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-500 [&::-webkit-scrollbar-track]:bg-gray-200">
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccessHandler}
              loading={<div className="p-4 text-gray-700">Loading PDF...</div>}
              error={<div className="p-4 text-red-500">Failed to load PDF. Please check the URL or try again.</div>}
              className="flex h-full flex-col"
            >
              {totalPages &&
                containerWidth &&
                Array.from(new Array(totalPages), (_el, index) => (
                  <div
                    key={`page_wrapper_${index + 1}`}
                    ref={(ref) => {
                      pageRefs.current[index] = ref;
                    }}
                    className="pdf-page-wrapper mb-6 flex h-auto w-full flex-col shadow-lg last:mb-0"
                  >
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={containerWidth * 0.95}
                      devicePixelRatio={window.devicePixelRatio || 2}
                      renderAnnotationLayer={true}
                    />
                  </div>
                ))}
            </Document>
          </div>
        </ZoomableImage>
      ) : (
        <div className="p-4 text-gray-500">No PDF URL provided.</div>
      )}
    </div>
  );
};

export default PDFCustomViewer;
